/**
 * Ad-click and GA attribution for the formation checkout.
 *
 * Reads the browser's own first-party cookies at checkout start and sends them
 * with `POST /api/customer/onboarding/complete`. The API freezes them into the
 * Stripe Checkout Session, and the paid webhook reports the purchase to the server
 * GTM container with them (apps/api/src/analytics/server-conversions.ts), so a
 * buyer who closes the tab or blocks scripts after paying is still attributed.
 *
 * Only ids, never identity: the GA client and session ids, the Google click ids
 * and Meta's browser and click ids. Every value is shape- and length-checked; a
 * malformed one is left out, and nothing here throws. The API validates again.
 *
 * Cookie consent (@nt/consent) decides what goes: the visitor's effective
 * analytics and advertising consent always travel (the server forwards nothing
 * the visitor refused, and sends no Meta purchase without advertising consent);
 * without analytics consent the GA ids stay home, without advertising consent
 * every Google click id and Meta id does.
 */

import { type ConsentSignal, readConsentState } from '@nt/consent'

export interface CheckoutAttribution {
  analytics_consent?: ConsentSignal
  ad_consent?: ConsentSignal
  ga_client_id?: string
  ga_session_id?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  fbp?: string
  fbc?: string
}

const CLICK_ID = /^[A-Za-z0-9._\-$]{1,200}$/
const GA_CLIENT_ID = /^\d{1,20}\.\d{1,20}$/
const GA_SESSION_ID = /^\d{1,20}$/
const FBP = /^fb\.\d\.\d{10,16}\.\d{1,30}$/
const FBC = /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_-]{1,180}$/
const FBCLID = /^[A-Za-z0-9_-]{1,180}$/

function parseCookies(cookieHeader: string): Map<string, string> {
  const cookies = new Map<string, string>()
  for (const part of cookieHeader.split(';')) {
    const index = part.indexOf('=')
    if (index <= 0) continue
    const name = part.slice(0, index).trim()
    let value = part.slice(index + 1).trim()
    try {
      value = decodeURIComponent(value)
    } catch {
      continue
    }
    if (name && !cookies.has(name)) cookies.set(name, value)
  }
  return cookies
}

function matching(value: string | undefined | null, shape: RegExp): string | undefined {
  const trimmed = value?.trim()
  return trimmed && shape.test(trimmed) ? trimmed : undefined
}

/** `_ga` = `GA1.<n>.<random>.<first visit>`; the client id is the last two parts. */
function gaClientId(cookie: string | undefined): string | undefined {
  const parts = cookie?.split('.') ?? []
  if (parts.length < 4 || !/^GA\d$/.test(parts[0] ?? '')) return undefined
  return matching(parts.slice(-2).join('.'), GA_CLIENT_ID)
}

/**
 * `_ga_<measurement id suffix>` holds the session: `GS1.1.<sid>.<count>.…` or
 * `GS2.1.s<sid>$o<count>$…`. Any suffix counts (the property differs per
 * environment); with several, the latest session (ids are epoch seconds) wins.
 */
function gaSessionId(cookies: Map<string, string>): string | undefined {
  let latest: string | undefined
  for (const [name, value] of cookies) {
    if (!/^_ga_[A-Za-z0-9]{1,20}$/.test(name)) continue
    const parts = value.split('.')
    let sid: string | undefined
    if (parts[0] === 'GS1') sid = parts[2]
    else if (parts[0] === 'GS2') sid = parts[2]?.split('$')[0]?.replace(/^s/, '')
    const clean = matching(sid, GA_SESSION_ID)
    if (clean && (!latest || Number(clean) > Number(latest))) latest = clean
  }
  return latest
}

/** `_gcl_aw` / `_gcl_gb` = `GCL.<timestamp>.<click id>`. */
function gclValue(cookie: string | undefined): string | undefined {
  const parts = cookie?.split('.') ?? []
  if (parts.length < 3 || parts[0] !== 'GCL') return undefined
  return matching(parts.slice(2).join('.'), CLICK_ID)
}

/**
 * Pure: the attribution in a cookie string and a URL query. Cookies win; the
 * query's click ids are the fallback (the app keeps no stored copy of them).
 */
export function parseCheckoutAttribution(cookieHeader: string, search = '', nowMs = Date.now()): CheckoutAttribution {
  const cookies = parseCookies(cookieHeader)
  let query: URLSearchParams
  try {
    query = new URLSearchParams(search)
  } catch {
    query = new URLSearchParams()
  }
  const fbclid = matching(query.get('fbclid'), FBCLID)
  const builtFbc = fbclid ? matching(`fb.1.${nowMs}.${fbclid}`, FBC) : undefined

  const consent = readConsentState(cookieHeader).effective
  const analytics = consent.analytics
  const advertising = consent.advertising

  const attribution: CheckoutAttribution = {
    analytics_consent: analytics ? 'granted' : 'denied',
    ad_consent: advertising ? 'granted' : 'denied',
    ...(analytics
      ? {
          ga_client_id: gaClientId(cookies.get('_ga')),
          ga_session_id: gaSessionId(cookies),
        }
      : {}),
    ...(advertising
      ? {
          gclid: gclValue(cookies.get('_gcl_aw')) ?? matching(query.get('gclid'), CLICK_ID),
          gbraid: gclValue(cookies.get('_gcl_gb')) ?? matching(query.get('gbraid'), CLICK_ID),
          wbraid: matching(query.get('wbraid'), CLICK_ID),
          fbp: matching(cookies.get('_fbp'), FBP),
          fbc: matching(cookies.get('_fbc'), FBC) ?? builtFbc,
        }
      : {}),
  }
  return Object.fromEntries(
    Object.entries(attribution).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  ) as CheckoutAttribution
}

/**
 * The current document's attribution (always with the consent), or undefined when
 * the cookies cannot be read. Never throws.
 */
export function readCheckoutAttribution(): CheckoutAttribution | undefined {
  try {
    if (typeof document === 'undefined') return undefined
    const search = typeof window !== 'undefined' ? window.location.search : ''
    return parseCheckoutAttribution(document.cookie, search)
  } catch {
    return undefined
  }
}
